<template>
  <div class="p-6 border rounded">
    <h3 class="mb-4">Time Settings</h3>
    <!-- Expiration Time -->
    <TimeDurationInput v-model="expirationTime" label="Expiration Time" />
    <!-- Reminder Time -->
    <TimeDurationInput v-model="reminderTime" label="Reminder Time" />
    <!-- Link After Done -->
    <div class="mt-4">
      <label class="block text-sm font-medium text-gray-700">Link After Done</label>
      <Select
        v-model="selectedLinkOption"
        :options="linkDropdownOptions"
        option-label="label"
        option-value="value"
        placeholder="Select or use custom"
        class="w-full"
        @change="onLinkDropdownChange"
      />
      <input
        v-if="isCustomLink"
        type="text"
        :value="customLinkValue"
        class="w-full border p-1 rounded mt-2"
        placeholder="/custom-path"
        @input="onCustomLinkInput"
      />
      <p v-else-if="selectedLinkLabel" class="text-sm text-gray-600 mt-2">
        Redirect: <strong>{{ selectedLinkLabel }}</strong>
      </p>
    </div>
    <div class="mt-4">
      <p class="text-sm text-gray-600">
        Current Expiration: {{ expirationTime }} ms ({{ expirationTimeInDays }} days)
      </p>
      <p class="text-sm text-gray-600">
        Current Reminder: {{ reminderTime }} ms ({{ reminderTimeInDays }} days)
      </p>
    </div>
  </div>
</template>

<script>
import TimeDurationInput from '../utils/TimeDurationInput.vue';
import Select from 'primevue/select';

const CUSTOM_VALUE = '__custom__';
// Option labels that trigger ?tab=<schemaName> appended when selected (stored in schema only). Comparison is trim + case-insensitive.
const LABELS_APPEND_TAB = ['Own Results', 'My Results Dashboard', 'My Results', 'Results'];

function normaliseLabelForAppendTab(label) {
  return (label || '').trim().toLowerCase();
}

function shouldAppendTabForLabel(label) {
  const n = normaliseLabelForAppendTab(label);
  return LABELS_APPEND_TAB.some((l) => normaliseLabelForAppendTab(l) === n);
}

export default {
  name: 'AssessmentSchemaTimeSettings',
  components: { TimeDurationInput, Select },
  props: {
    // Expecting values to be in milliseconds.
    modelExpirationTime: {
      type: Number,
      default: 0,
    },
    modelReminderTime: {
      type: Number,
      default: 0,
    },
    modelLinkAfterDone: {
      type: String,
      default: '',
    },
    linkAfterAssessmentOptions: {
      type: Array,
      default: () => [],
    },
    /** Schema name used to append ?tab=<schemaName> when a result-dashboard path is selected (saved in schema only) */
    schemaName: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelExpirationTime', 'update:modelReminderTime', 'update:modelLinkAfterDone'],
  data() {
    return {
      selectedLinkOption: null,
      customLinkValue: '',
    };
  },
  computed: {
    expirationTime: {
      get() {
        return this.modelExpirationTime;
      },
      set(newVal) {
        this.$emit('update:modelExpirationTime', newVal);
      },
    },
    reminderTime: {
      get() {
        return this.modelReminderTime;
      },
      set(newVal) {
        this.$emit('update:modelReminderTime', newVal);
      },
    },
    linkDropdownOptions() {
      const opts = (this.linkAfterAssessmentOptions || []).map((o) =>
        typeof o === 'string' ? { label: o, value: o } : { label: o.label || o.value, value: o.value },
      );
      opts.push({ label: 'Custom path…', value: CUSTOM_VALUE });
      return opts;
    },
    isCustomLink() {
      return this.selectedLinkOption === CUSTOM_VALUE;
    },
    /** Label of the selected option (e.g. "Own Results") for display in time settings view */
    selectedLinkLabel() {
      if (!this.selectedLinkOption || this.selectedLinkOption === CUSTOM_VALUE) return '';
      const opt = this.linkDropdownOptions.find((o) => o.value === this.selectedLinkOption);
      return opt ? (opt.label || '') : '';
    },
    // For display, convert ms to days.
    expirationTimeInDays() {
      return (this.expirationTime / (24 * 60 * 60 * 1000)).toFixed(2);
    },
    reminderTimeInDays() {
      return (this.reminderTime / (24 * 60 * 60 * 1000)).toFixed(2);
    },
  },
  watch: {
    modelLinkAfterDone: {
      immediate: true,
      handler(val) {
        this.syncLinkFromModel(val);
      },
    },
    linkAfterAssessmentOptions: {
      handler() {
        this.syncLinkFromModel(this.modelLinkAfterDone);
      },
    },
    schemaName: {
      handler() {
        if (this.selectedLinkOption && this.selectedLinkOption !== CUSTOM_VALUE) {
          this.onLinkDropdownChange();
        }
      },
    },
  },
  methods: {
    /** Strip ?tab=... for matching stored value to dropdown option (base path) */
    basePathForMatch(val) {
      const v = (val || '').trim() || '/';
      const q = v.indexOf('?');
      return q >= 0 ? v.slice(0, q) : v;
    },
    syncLinkFromModel(val) {
      const v = (val || '').trim() || '/';
      const base = this.basePathForMatch(v);
      const opts = this.linkDropdownOptions.filter((o) => o.value !== CUSTOM_VALUE);
      const found = opts.find((o) => o.value === base);
      if (found) {
        this.selectedLinkOption = found.value;
        this.customLinkValue = '';
      } else {
        this.selectedLinkOption = CUSTOM_VALUE;
        this.customLinkValue = v;
      }
    },
    effectiveValueForOption(optionValue) {
      if (optionValue === CUSTOM_VALUE) return this.customLinkValue || '/';
      const opt = this.linkDropdownOptions.find((o) => o.value === optionValue);
      const schemaNameToAppend = (this.schemaName || '').trim() || 'assessment';
      if (opt && shouldAppendTabForLabel(opt.label)) {
        const sep = (optionValue || '').includes('?') ? '&' : '?';
        return (optionValue || '/') + sep + 'tab=' + encodeURIComponent(schemaNameToAppend);
      }
      return optionValue || '/';
    },
    onLinkDropdownChange() {
      const valueToEmit = this.effectiveValueForOption(this.selectedLinkOption);
      this.$emit('update:modelLinkAfterDone', valueToEmit);
    },
    onCustomLinkInput(e) {
      this.customLinkValue = e.target.value;
      this.$emit('update:modelLinkAfterDone', this.customLinkValue || '/');
    },
  },
};
</script>

<style scoped>
/* Additional styling if needed */
</style>
